import { useId, useRef } from "react";
import { useProgressStore } from "@/application/stores/progressStore";
import { useSkillMemoryStore } from "@/application/stores/skillMemoryStore";
import { createSkillMemory } from "@/domain/learning/skill-memory";
import type { SkillMemoryRepository } from "@/domain/learning/skill-memory-repository";
import { createProgress, TOTAL_LEVELS } from "@/domain/progress/model/progress";
import type { ProgressRepository } from "@/domain/progress/progress-repository";
import { createProgressRepository } from "@/infrastructure/persistence/progress-repository-factory";
import { createSkillMemoryRepository } from "@/infrastructure/persistence/skill-memory-repository-factory";
import { Button } from "@/presentation/components/Button";
import { Modal } from "@/presentation/components/overlay/Modal";
import { LABELS, LEVEL_RANK_NAMES } from "@/presentation/i18n/labels";
import "./ProgressResetModal.css";

interface ProgressResetModalProps {
  readonly open: boolean;
  readonly onClose: () => void;
}

/**
 * 進捗リセット確認モーダル（O-2。ui-design.md §7.7 / screen-flow.md §2.3）。
 * 帳（設定, O-1 = `SettingsPanel`）の「記録の消去」から開く想定。`open`/`onClose`は
 * `SettingsPanel`側がローカルstateで管理して渡す（またはこのモーダルを直接
 * `useOverlay()`の`modal`経由で開いてもよい — どちらでも成立するように`open`/`onClose`の
 * 素朴なpropsだけを要求する形にしてある）。
 *
 * D-105（絶対ルール）: 破壊的操作でも`--shu`（朱）を一切使わない。安全側の「やめる」を
 * `variant="primary"`（唯一の主たる押し）にし、破壊側の「消す」は`variant="secondary"`の
 * 控えめな見た目にすることで、色ではなく「確認文言・既定フォーカス・視覚的な優先度の逆転」で
 * 危険度を伝える。
 *
 * 既定フォーカスは「やめる」（計画13の完了条件）。`Modal`の`useFocusTrap`は開いた時点で
 * コンテナ内の最初の フォーカス可能要素へ自動でフォーカスするため、「やめる」をDOM順で
 * 先頭に置くだけで満たせる（`ProgressResetModal.test.tsx`で実測して確認している）。
 */
export function ProgressResetModal({ open, onClose }: ProgressResetModalProps) {
  const titleId = useId();
  const progress = useProgressStore((state) => state.progress);
  const setProgress = useProgressStore((state) => state.setProgress);
  const setSkillMemory = useSkillMemoryStore((state) => state.setMemory);

  const repositoryRef = useRef<ProgressRepository | null>(null);
  if (repositoryRef.current === null) {
    repositoryRef.current = createProgressRepository();
  }
  const skillMemoryRepositoryRef = useRef<SkillMemoryRepository | null>(null);
  if (skillMemoryRepositoryRef.current === null) {
    skillMemoryRepositoryRef.current = createSkillMemoryRepository();
  }

  const stampCount = progress.stamps.size;
  const grandRankName = LEVEL_RANK_NAMES[TOTAL_LEVELS];
  const bodyText =
    stampCount === 0
      ? "まだ朱印はありませんが、ここまでの稽古の記録が失われます。"
      : `授かった${LABELS.stamp} ${stampCount} つと${grandRankName}すべての進みが失われ、元に戻せません。`;

  function handleConfirm() {
    repositoryRef.current?.clear();
    skillMemoryRepositoryRef.current?.clear();
    setProgress(createProgress());
    setSkillMemory(createSkillMemory());
    onClose();
  }

  return (
    <Modal open={open} onClose={onClose} titleId={titleId}>
      <h2 id={titleId} className="progress-reset-modal__title">
        稽古の記録をすべて消しますか？
      </h2>
      <p className="progress-reset-modal__body">{bodyText}</p>
      <div className="progress-reset-modal__actions">
        <Button variant="primary" onClick={onClose}>
          やめる
        </Button>
        <Button variant="secondary" onClick={handleConfirm}>
          消す
        </Button>
      </div>
    </Modal>
  );
}
